import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TutorialType } from "components/Tutorial/components/TutorialItem";
import { AccountSelectMenu } from "pages/Account/accountMenu";
import { EventDispatchLogout } from "router/events";
import { INotifInfo, IPaymentProblemDetail } from "api/user/user.type";
import { IPageStateSnapshot } from "api/chatbot/chatbot.type";
import { ModulePermissions } from "types/permissions";

interface ISnackbar {
  id: string;
  message: string;
  icon: any;
  type?: SnackbarType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  persistent?: boolean;
  createdAt: number;
}

export enum SnackbarType {
  Success,
  Fail,
  Warning,
  Info,
}

// Locally-tracked moodboard submission still being processed by the BE.
// Lives in commonSlice because the create flow (/moodboards/new) and the
// rendering surface (MoodboardsPanel inside DesignStudio) are different
// page contexts that need to share state across navigations.
export interface IPendingMoodboard {
  id: string; // local uuid — used until BE assigns a real one
  name: string;
  thumbnails: string[]; // up to 3 image URLs for the card preview
  createdAt: number;
}

export const commonSlice = createSlice({
  name: "common",
  initialState: {
    isDesignTutorialCompleted: false,
    isEditTutorialCompleted: false,
    isTrainTutorialCompleted: false,
    isMainMenuFixed: false,
    // Desktop left-nav collapsed state. Shared so the collapse toggle can live in the page
    // header (PageTitleBar) while the SideMenu reads it to render the icon-only rail.
    isSidebarCollapsed: false,
    // Runtime override for the global top bar, set by pages/sub-views whose needs differ from their
    // route default (e.g. moodboard detail hides it; create intros want agent-only). Null = defer to
    // the route-based rule in Layout. Must be cleared on unmount.
    topBarMode: null as "full" | "agent" | "hidden" | null,
    // Credit balance cached globally so the avatar dropdown shows it instantly on open instead of
    // flashing empty while GET /user-detail resolves. Populated once on first mount, refreshed on open.
    credits: {
      loaded: false,
      design: { total: 0, used: 0 },
      designBonus: 0,
      photoshoot: { total: 0, used: 0 },
    },
    showSearchBar: true,
    showImagePopup: false,
    imagePopupItemId: null,
    imagePopupImageIdx: null as any,
    showEditProfileModal: false,
    showChangePasswordModal: false,
    showDeleteAccountModal: false,
    showUserFlow: false,
    isAuthorized: false,
    // `GET /v1/users/{id}` failed (503 `permissions.unavailable`, other 5xx, network) while
    // `user.permissions` was still unresolved — the "couldn't load your permissions, retry"
    // state (`[PERMISSION_LOADING_FALLBACK]`). Cleared by the next `setUser` carrying
    // `permissions`. Set only by `hooks/useUserDetailRefresh`.
    permissionsError: false,
    user: {
      id: "" as any,
      name: "",
      username: "",
      email: "",
      work: "",
      privateSession: 0,
      createPeriod: 120,
      profilePhoto: "",
      packageCode: "",
      permissions: undefined as ModulePermissions | undefined,
      privacy_perm: null as number | null,
      demoInfo: undefined as
        | {
            is_design: boolean;
            is_mannequin: boolean;
            is_video: boolean;
            is_techpack: boolean;
            is_trend: boolean;
            end_try_date: string;
          }
        | undefined,
      notifInfo: undefined as INotifInfo[] | undefined,
      language: undefined as string | undefined,
      // From `GET /users/{id}` via `mapUserDetailToRedux` (STRIPE Phase 3 / D2) — see `utils/planStatus`.
      hasPaymentProblem: false,
      canManageBilling: false,
      // P7 F2-B6: decline reason + manager-only pay link. Detail only; never a gate (the gate is `hasPaymentProblem`).
      paymentProblem: null as IPaymentProblemDetail | null,
    },
    isCheckingToken: true,
    // STRIPE P5-F2 (PD7): the session belongs to a suspended team — the app renders the suspended-team screen instead.
    teamSuspended: false,
    searchInput: { text: "", image: "" },
    noReset: false,
    snackbar: [] as ISnackbar[],
    tutorial: { show: false, type: null as TutorialType | null, data: [] as any },
    accountSelectedMenu: AccountSelectMenu.AccountSettings,
    showUpdateModal: false,
    showFeatureAnnouncementPopup: false,
    showWebinarModal: false,
    browzwear: {
      apiKey: "",
      apiSecret: "",
      storeName: "",
      email: "",
      ownerId: "",
      isConnected: false,
    },
    isChatbotOpen: false,
    currentPageState: null as IPageStateSnapshot | null,
    // Moodboards the user has just submitted via Create Moodboard that are still
    // generating on the backend. Rendered as a "Processing…" card in
    // MoodboardsPanel at the top of the grid so the user gets immediate feedback.
    // BE doesn't yet return a job_id for trendAnalysis, so we mint a local id
    // and clear entries when their name shows up in the next list fetch.
    pendingMoodboards: [] as IPendingMoodboard[],
  },
  reducers: {
    setAccountSelectedMenu: (state, action) => {
      state.accountSelectedMenu = action.payload;
    },
    setIsDesignTutorialCompleted: (state, action) => {
      state.isDesignTutorialCompleted = action.payload;
    },
    setIsEditTutorialCompleted: (state, action) => {
      state.isEditTutorialCompleted = action.payload;
    },
    setIsTrainTutorialCompleted: (state, action) => {
      state.isTrainTutorialCompleted = action.payload;
    },
    setTutorial: (state, action) => {
      state.tutorial = action.payload;
    },
    toggleSearchBar: (state) => {
      state.showSearchBar = !state.showSearchBar;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isSidebarCollapsed = action.payload;
    },
    setCredits: (state, action: PayloadAction<{ design: { total: number; used: number }; designBonus: number; photoshoot: { total: number; used: number } }>) => {
      state.credits = { loaded: true, ...action.payload };
    },
    setTopBarMode: (state, action: PayloadAction<"full" | "agent" | "hidden" | null>) => {
      state.topBarMode = action.payload;
    },
    toggleSidebar: (state) => {
      state.isSidebarCollapsed = !state.isSidebarCollapsed;
    },
    setShowSearchBar: (state, action) => {
      state.showSearchBar = action.payload;
    },
    toggleImagePopup: (state) => {
      state.showImagePopup = !state.showImagePopup;
    },
    setImagePopupItemId: (state, action) => {
      state.imagePopupItemId = action.payload;
    },
    setImagePopupImageIdx: (state, action) => {
      state.imagePopupImageIdx = action.payload;
    },
    toggleEditProfileModal: (state) => {
      state.showEditProfileModal = !state.showEditProfileModal;
    },
    toggleChangePasswordModal: (state) => {
      state.showChangePasswordModal = !state.showChangePasswordModal;
    },
    toggleDeleteAccountModal: (state) => {
      state.showDeleteAccountModal = !state.showDeleteAccountModal;
    },
    toggleUserFlow: (state) => {
      state.showUserFlow = !state.showUserFlow;
    },
    setShowUserFlow: (state, action) => {
      state.showUserFlow = action.payload;
    },
    setIsAuthorized: (state, action) => {
      state.isAuthorized = action.payload;
    },
    setUser: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
      if (action.payload?.permissions !== undefined) state.permissionsError = false;
    },
    setPermissionsError: (state, action: PayloadAction<boolean>) => {
      state.permissionsError = action.payload;
    },
    signOut: () => {
      window.dispatchEvent(new CustomEvent(EventDispatchLogout));
    },
    setCheckingToken: (state, action) => {
      state.isCheckingToken = action.payload;
    },
    setTeamSuspended: (state, action: PayloadAction<boolean>) => {
      state.teamSuspended = action.payload;
    },

    setSearchInput: (state, action) => {
      state.searchInput.text = action.payload.text;
      state.searchInput.image = action.payload.image;
    },
    setNoReset: (state, action) => {
      state.noReset = action.payload;
    },
    setSnackbar: (state, action) => {
      const id = action.payload?.id || `snackbar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newSnackbar: ISnackbar = {
        id,
        message: action.payload?.message || "",
        icon: action.payload?.icon,
        type: action.payload?.type ?? SnackbarType.Info,
        duration: action.payload?.duration,
        action: action.payload?.action,
        persistent: action.payload?.persistent ?? false,
        createdAt: Date.now(),
      };

      // Limit to 10 snackbars max
      const updatedSnackbars = [...state.snackbar, newSnackbar];
      if (updatedSnackbars.length > 10) {
        updatedSnackbars.shift();
      }

      state.snackbar = updatedSnackbars;
    },
    removeSnackbar: (state, action) => {
      state.snackbar = state.snackbar.filter((item) => item.id !== action.payload);
    },
    resetSnackbar: (state) => {
      state.snackbar = [];
    },
    showUpdateModal: (state) => {
      state.showUpdateModal = true;
    },
    hideUpdateModal: (state) => {
      state.showUpdateModal = false;
    },
    showFeatureAnnouncementPopup: (state) => {
      state.showFeatureAnnouncementPopup = true;
    },
    hideFeatureAnnouncementPopup: (state) => {
      state.showFeatureAnnouncementPopup = false;
    },
    showWebinarModal: (state) => {
      state.showWebinarModal = true;
    },
    hideWebinarModal: (state) => {
      state.showWebinarModal = false;
    },
    setBrowzwear: (state, action) => {
      state.browzwear = { ...state.browzwear, ...action.payload };
    },
    setChatbotOpen: (state, action) => {
      state.isChatbotOpen = action.payload;
    },
    setCurrentPageState: (state, action: PayloadAction<IPageStateSnapshot | null>) => {
      state.currentPageState = action.payload;
    },
    clearCurrentPageState: (state) => {
      state.currentPageState = null;
    },
    // Pending moodboards (Create Moodboard submission, BE still generating)
    addPendingMoodboard: (state, action: PayloadAction<IPendingMoodboard>) => {
      state.pendingMoodboards.push(action.payload);
    },
    removePendingMoodboard: (state, action: PayloadAction<string>) => {
      state.pendingMoodboards = state.pendingMoodboards.filter((m) => m.id !== action.payload);
    },
    // Sweep pending entries whose names now appear in the BE list. Local
    // pendings exist solely for the ~1-2s window between submit and the next
    // panel fetch — once the BE returns an in-progress row (status === 0), the
    // MoodboardCard renders it with its own Processing overlay and the local
    // pending is redundant. No grace window needed.
    reconcilePendingMoodboards: (state, action: PayloadAction<string[]>) => {
      const seen = new Set(action.payload.map((n) => n.trim().toLowerCase()));
      state.pendingMoodboards = state.pendingMoodboards.filter((m) => !seen.has(m.name.trim().toLowerCase()));
    },
  },
  extraReducers: {},
});

export const {
  setPermissionsError,
  setIsDesignTutorialCompleted,
  setIsEditTutorialCompleted,
  setIsTrainTutorialCompleted,
  setTutorial,
  toggleSearchBar,
  setSidebarCollapsed,
  toggleSidebar,
  setCredits,
  setTopBarMode,
  toggleImagePopup,
  setImagePopupItemId,
  setImagePopupImageIdx,
  toggleEditProfileModal,
  toggleChangePasswordModal,
  toggleDeleteAccountModal,
  toggleUserFlow,
  setIsAuthorized,
  setUser,
  setShowUserFlow,
  signOut,
  setCheckingToken,
  setTeamSuspended,
  setSearchInput,
  setShowSearchBar,
  setNoReset,
  setSnackbar,
  removeSnackbar,
  resetSnackbar,
  setAccountSelectedMenu,
  showUpdateModal,
  hideUpdateModal,
  showFeatureAnnouncementPopup,
  hideFeatureAnnouncementPopup,
  showWebinarModal,
  hideWebinarModal,
  setBrowzwear,
  setChatbotOpen,
  setCurrentPageState,
  clearCurrentPageState,
  addPendingMoodboard,
  removePendingMoodboard,
  reconcilePendingMoodboards,
} = commonSlice.actions;

export default commonSlice.reducer;
